import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useApplicationContext } from "../../context/ApplicationContext";

import {
  ArrowBackOutlined,
  HelpOutline,
  HomeOutlined,
  KeyboardArrowDownOutlined,
  LogoutOutlined,
  Search,
  Store,
  TaskAltOutlined,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LuMoon, LuSun, LuZap } from "react-icons/lu";
import {
  AI_Entity,
  Entity_Label,
  ModulePath,
} from "../../constant/ApplicationVariables";

import { LightTooltip } from "../styled/CustomComponent";
import ApplicationLogo from "./ApplicationLogo";
import MediaComponent from "../media/MediaComponent";
import {
  DashboardCustomIcon,
  ExecutionCustomIcon,
  NewRegressionCustomIcon,
  OrganisationCustomIcon,
  PlanningCustomIcon,
  ProgramCustomIcon,
  ProjectCustomIcon,
  TasksCustomIcon,
  TestCaseCustomIcon,
  UseCaseCustomIcon,
  UserStoryCustomIcon,
} from "../styled/CustomIcons";
import { useHotkeys } from "react-hotkeys-hook";
import { KeyboardShortcuts } from "../../constant/KeyboardKeys";
import { useTranslation } from "react-i18next";
import { memo, lazy } from "react";
import { Suspense } from "react";
import { font } from "../../styles/font";
import { toggleTheme, initializeTheme } from "../../utils/themeUtils";
const StyledGradientButton = memo(
  lazy(() => import("../styled/CustomComponent")),
);

const ApplicationHeader = memo(() => {
  const {
    activePage,
    setActivePage,
    headerSearchText,
    setHeaderSearchText,
    profileDetails,
    toggleFolderSidebar,
    setToggleFolderSidebar,
    toggleChatWindow,
    setToggleChatWindow,
    activeProject,
    setActiveProject,
    handleRecentProjectSelect,
    setFolderStructureList,
    setUserStoryList,
    currentFolderData,
    setCurrentFolderData,
    handleLogout,
    isItemChatWindow,
    setIsItemChatWindow,
    currentUserStoryData,
    setCurrentUserStoryData,
    currentUseCaseData,
    setCurrentUseCaseData,
    openCreateStoryDialog,
    setOpenCreateStoryDialog,
    openEditStoryDialog,
    setOpenEditStoryDialog,
    openDeleteStoryDialog,
    setOpenDeleteStoryDialog,
    openCreateUseCaseDialog,
    setOpenCreateUseCaseDialog,
    openEditUseCaseDialog,
    setOpenEditUseCaseDialog,
    openDeleteUseCaseDialog,
    setOpenDeleteUseCaseDialog,
    getAILabel,
    userDetail,
    setUserDetail,
    currentAIEntityData,
    setCurrentAIEntityData,
  } = useApplicationContext();
  const theme = useTheme();
  const location = useLocation();
  const pathName = location.pathname;

  const isHome = Boolean(pathName.match(/^\/project/));
  const navigate = useNavigate();
  const { t } = useTranslation("translation");

  // Enhanced responsive breakpoints
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  // Enhanced responsive check
  useEffect(() => {
    const checkResponsive = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768); // Mobile: < 768px
      setIsTablet(width >= 768 && width < 1024); // Tablet: 768px - 1024px
      setIsDesktop(width >= 1024); // Desktop: ≥ 1024px
    };

    checkResponsive();
    window.addEventListener("resize", checkResponsive);
    return () => window.removeEventListener("resize", checkResponsive);
  }, []);

  // Hotkeys
  useHotkeys(KeyboardShortcuts.header.search, (e) => {
    console.log("Search hotkey pressed");
  });

  useHotkeys(KeyboardShortcuts.ai.toggle, (e) => {
    handleAIWindow();
  });
  useHotkeys(KeyboardShortcuts.header.toggleSidebar, (e) => {
    setToggleFolderSidebar((prevSelected) => !prevSelected);
  });

  const searchRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activePageTab, setActivePageTab] = useState(ModulePath.requirements);
  const [headerPagesList, setHeaderPagesList] = useState([]);
  const [recentProjectsList, setRecentProjectsList] = useState(() => {
    return JSON.parse(localStorage.getItem("recentProjects")) || [];
  });
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const hideRecentProjects = location.pathname.startsWith(ModulePath.projects);

  const isMarketplacePage = location.pathname === ModulePath.marketplace;

  const isUnactiveProjectPage = [
    ModulePath.projects,
    ModulePath.programmes,
    ModulePath.organisations,
    ModulePath.settings,
  ].includes(pathName);

  const isActiveProjectPageWithoutBackButton = [
    ModulePath.task,
    ModulePath.regression,
    ModulePath.configuration,
  ].includes(pathName);

  const handleClickProfilePopup = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const getAIButtonText = () => {
    return toggleChatWindow ? "Close AI" : getAILabel();
  };

  useEffect(() => {
    const storedRecentProjects =
      JSON.parse(localStorage.getItem("recentProjects")) || [];
    setRecentProjectsList(storedRecentProjects);
  }, []);

  const handlePageClick = (page) => {
    if (pathName !== page.path) {
      page.onClick();
      navigate(page.path);
      if (currentFolderData) {
        setCurrentFolderData(null);
      }
    }
  };

  const handleAIWindow = () => {
    setToggleChatWindow((prevSelected) => !prevSelected);
    if (currentAIEntityData?.entity === AI_Entity.folder) {
      setCurrentAIEntityData(null);
    }
  };

  // Navigation pages
  useEffect(() => {
    let array = [];

    setCurrentUserStoryData(null);
    setCurrentUseCaseData(null);
    setIsItemChatWindow(false);
    setActivePageTab(pathName);

    if (isUnactiveProjectPage) {
      array = [
        {
          name: Entity_Label.project,
          path: ModulePath.projects,
          icon: <ProjectCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.programme,
          path: ModulePath.programmes,
          icon: <ProgramCustomIcon />,
          onClick: () => {},
          style: {},
          disabled: true,
        },
        {
          name: Entity_Label.organisation,
          path: ModulePath.organisations,
          icon: <OrganisationCustomIcon />,
          onClick: () => {},
          style: {},
          disabled: true,
        },
      ];
    } else if (pathName === ModulePath.superAdmin) {
      array = [];
    } else {
      array = [
        {
          name: Entity_Label.userStory,
          path: ModulePath.requirements,
          icon: <UserStoryCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.useCase,
          path: ModulePath.useCase,
          icon: <UseCaseCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.testCase,
          path: ModulePath.testCase,
          icon: <TestCaseCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.planning,
          path: ModulePath.planning,
          icon: <PlanningCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.execution,
          path: ModulePath.execution,
          icon: <ExecutionCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.dashboard,
          path: ModulePath.dashboard,
          icon: <DashboardCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.tasks,
          path: ModulePath.task,
          icon: <TasksCustomIcon />,
          onClick: () => {},
          style: {},
        },
        {
          name: Entity_Label.regression,
          path: ModulePath.regression,
          icon: <NewRegressionCustomIcon />,
          onClick: () => {},
          style: {},
        },
      ];
    }

    setHeaderPagesList(array);
  }, [pathName]);

  // Responsive text display
  const getTabDisplay = (pageName) => {
    if (isMobile) return ""; // Icons only on mobile
    if (isTablet) {
      // Short versions for tablet
      const shortNames = {
        UserStory: "Story",
        UseCase: "Use Case",
        TestCase: "Test",
        Planning: "Plan",
        Execution: "Exec",
        Dashboard: "Dash",
        Project: "Proj",
        Programme: "Prog",
        Organisation: "Org",
      };
      return shortNames[pageName] || pageName.substring(0, 8);
    }
    return pageName; // Full names on desktop
  };

  // Responsive icon sizes
  const getIconSize = () => {
    if (isMobile) return "small";
    return "medium";
  };

  // Responsive font sizes
  const getFontSize = (type) => {
    if (isMobile) {
      return {
        projectName: "0.7rem",
        tab: "0.6rem",
        userInfo: "0.75rem",
        userRole: "0.65rem",
      }[type];
    }
    if (isTablet) {
      return {
        projectName: "0.75rem",
        tab: "0.7rem",
        userInfo: "0.8rem",
        userRole: "0.7rem",
      }[type];
    }
    return {
      projectName: "0.8rem",
      tab: "0.75rem",
      userInfo: "0.875rem",
      userRole: "0.75rem",
    }[type];
  };

  // Initialize theme on mount
  useEffect(() => {
    initializeTheme();
  }, []);

  // Theme toggle handler
  const handleThemeToggle = () => {
    toggleTheme();
  };

  return (
    <Box
      className="h-full w-full flex flex-col bg-background text-foreground"
      sx={{ borderBottom: "1px solid hsl(var(--border))" }}
    >
      <Box
        className="h-full w-full flex items-center justify-between"
        sx={{
          px: isMobile ? 1.5 : isTablet ? 2 : 3,
          py: 1.5,
        }}
      >
        <Box className="flex gap-6">
          {/* Left Section - Logo and Project */}
          <Box className="flex items-center  flex-shrink-0 ">
            {/* Logo */}
            {/* {isHome && (
            <Box className="flex items-center ">
              <ApplicationLogo
                sx={{
                  width: isMobile ? 64 : 80,
                  height: isMobile ? 64 : 80,
                  color: "hsl(var(--primary))",
                }}
              />
            </Box>
          )} */}

            {isHome && (
              <Link
                // to="/"

                className="inline-flex items-center   group"
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan to-emerald blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-300" />
                </div>
                <span className="text-xl font-extrabold tracking-tight  text-black dark:text-white">
                  Flox
                  <span className="inline-block text-gradient relative">
                    pad
                  </span>
                </span>
              </Link>
            )}

            {/* Project Selector */}
            {!hideRecentProjects && (
              <Box className="flex items-center">
                <LightTooltip
                  title={activeProject?.name || "Recent Projects"}
                  placement="bottom-start"
                >
                  <Box
                    onClick={handleClick}
                    className="flex items-center cursor-pointer 
                   text-foreground 
                   duration-200"
                    sx={{
                      height: 36,
                      px: "10px",
                      borderRadius: "8px",
                      minWidth: 0,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "hsl(var(--muted))",
                        outline: "1px solid hsl(var(--border))",
                      },
                    }}
                  >
                    {/* Project Icon */}
                    <Box className="flex items-center flex-shrink-0 mr-2">
                      <ProjectCustomIcon
                        sx={{
                          color: theme.palette.icon.main,
                          fontSize: isMobile ? "1.2rem" : "1.4rem",
                        }}
                      />
                    </Box>

                    {/* Project Name - Responsive */}
                    {!isMobile && (
                      <Box className="flex-1 min-w-0 mr-2">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: isTablet ? "120px" : "160px",
                            fontSize: getFontSize("projectName"),
                          }}
                        >
                          {activeProject?.name || "Recent Projects"}
                        </Typography>
                      </Box>
                    )}

                    {/* Dropdown Arrow */}
                    <KeyboardArrowDownOutlined
                      fontSize="small"
                      sx={{
                        color: theme.palette.text.secondary,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                </LightTooltip>

                {/* Projects Menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: {
                      borderRadius: "8px",
                      mt: 1,
                      width: 260,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* Active Project */}
                  {activeProject && (
                    <MenuItem
                      selected
                      onClick={() => {
                        handleRecentProjectSelect(activeProject);
                        handleClose();
                      }}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: theme.palette.action.selected,
                        },
                        py: 1.2,
                        borderRadius: "6px",
                        mx: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ProjectCustomIcon
                          fontSize="small"
                          sx={{ color: theme.palette.secondary.main }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={activeProject.name}
                        primaryTypographyProps={{
                          sx: {
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          },
                        }}
                      />
                    </MenuItem>
                  )}

                  {/* Recent Projects Header */}
                  {recentProjectsList && recentProjectsList.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Recent Projects
                      </Box>
                    </>
                  )}

                  {/* Recent Projects List */}
                  {recentProjectsList && recentProjectsList.length > 0 ? (
                    recentProjectsList
                      .filter((p) => p.name !== activeProject?.name)
                      .slice(0, 5)
                      .map((project, index) => (
                        <MenuItem
                          key={project.id || project.name || index}
                          onClick={() => {
                            handleRecentProjectSelect(project);
                            handleClose();
                          }}
                          sx={{
                            py: 1,
                            mx: 0.5,
                            borderRadius: "6px",
                          }}
                        >
                          <ListItemText
                            primary={project.name}
                            primaryTypographyProps={{
                              sx: {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: "0.8rem",
                                pl: 0.5,
                              },
                            }}
                          />
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem
                      disabled
                      sx={{
                        color: theme.palette.text.disabled,
                        px: 2,
                        py: 1.5,
                        fontSize: "0.8rem",
                      }}
                    >
                      No recent projects
                    </MenuItem>
                  )}

                  <Divider sx={{ my: 1 }} />

                  {/* View All Projects */}
                  <MenuItem
                    onClick={() => {
                      navigate(ModulePath.projects);
                      handleClose();
                    }}
                    sx={{
                      py: 1.2,
                      mx: 0.5,
                      mb: 0.5,
                      borderRadius: "6px",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <HomeOutlined
                        fontSize="small"
                        sx={{ color: theme.palette.secondary.main }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="View All Projects"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>

          {/* Middle Section - Navigation Tabs with Underline Animation */}
          <Box className="flex-1 flex justify-center items-center min-w-0">
            <Box
              className="flex items-center overflow-x-auto"
              sx={{
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {pathName !== ModulePath.superAdmin &&
                headerPagesList?.map((page, index) => {
                  const isActive = page?.path === activePageTab;
                  const isDisabled = page?.disabled;
                  const displayText = getTabDisplay(page?.name);

                  return (
                    <LightTooltip title={page?.name} key={index}>
                      <Box
                        sx={{
                          position: "relative",
                          mx: 0.5,
                          my: 1,
                        }}
                      >
                        <IconButton
                          disabled={isDisabled}
                          onClick={
                            isDisabled ? undefined : () => handlePageClick(page)
                          }
                          sx={{
                            minWidth: isMobile ? 50 : isTablet ? 100 : 110,
                            height: 36,
                            borderRadius: "6px",
                            backgroundColor: "transparent",
                            color: isActive
                              ? "hsl(var(--foreground))" // active = white in dark
                              : "hsl(var(--muted-foreground))",
                            ...(isDisabled && {
                              opacity: 0.4,
                              cursor: "not-allowed",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }),
                          }}
                        >
                          {/* Icon */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {page.icon}
                            {/* Text - responsive display */}
                            {!isMobile && displayText && (
                              <Typography
                                sx={{
                                  fontSize: font.size.fs_4,
                                  fontWeight: isActive ? 700 : 500,
                                  ml: 0.5,
                                  whiteSpace: "nowrap",
                                  color: isActive
                                    ? "hsl(var(--foreground))"
                                    : "hsl(var(--muted-foreground))",
                                }}
                              >
                                {displayText}
                              </Typography>
                            )}
                          </Box>
                        </IconButton>

                        {/* Animated Underline */}
                        {isActive && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: "55%",
                              transform: "translateX(-50%)",
                              width: "50%",
                              height: "3px",
                              borderRadius: "2px",
                              backgroundColor: "hsl(var(--primary))",
                              animation:
                                "underlineExpand 0.3s ease-out forwards",
                            }}
                          />
                        )}
                      </Box>
                    </LightTooltip>
                  );
                })}
            </Box>
          </Box>
        </Box>

        <Button
          variant="contained"
            onClick={() => window.open(ModulePath.marketplace, '_blank')}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            height: 40,
            px: 3,
            display: "flex",
            ml: "auto",
            gap: 1,
            alignItems: "center",
            mr: 1,

            background: isMarketplacePage
              ? "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--emerald)))"
              : "hsl(var(--background))",

            color: isMarketplacePage
              ? "hsl(var(--primary-foreground))"
              : "hsl(var(--foreground))",

            // ACTIVE BORDER
            border: isMarketplacePage
              ? "1px solid hsl(var(--cyan))"
              : "1px solid hsl(var(--emerald) /0.65)",

            boxShadow: isMarketplacePage
              ? "0 4px 14px hsl(var(--cyan) / 0.35)"
              : "none",

            transition: "all 0.25s ease",

            "&:hover": {
              background: isMarketplacePage
                ? "linear-gradient(90deg, hsl(var(--cyan) / 0.9), hsl(var(--emerald) / 0.9))"
                : "hsl(var(--muted))",

              border: isMarketplacePage
                ? "1px solid hsl(var(--cyan))"
                : "1px solid hsl(var(--emerald) /0.45)",

              boxShadow: isMarketplacePage
                ? "0 6px 20px hsl(var(--cyan) / 0.45)"
                : "0 2px 10px hsl(var(--foreground) / 0.08)",
            },
          }}
        >
          <Store className="h-5 w-5" />
          Marketplace
        </Button>

        {/* Right Section - Profile and Actions */}
        <Box className="flex items-center gap-2 flex-shrink-0">
          {/* AI Button */}
          {false && !isUnactiveProjectPage && !isMobile && (
            <LightTooltip title={`${getAIButtonText()}`}>
              <Suspense fallback={<CircularProgress size={20} />}>
                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={() => handleAIWindow()}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: isTablet ? 2 : 2.5,
                    fontSize: isTablet ? "0.75rem" : "0.8rem",
                    backgroundColor: theme.palette.ai.main,
                    "&:hover": {
                      backgroundColor: theme.palette.ai.dark,
                    },
                  }}
                >
                  {isTablet ? "AI" : "AI Assistant"}
                </Button>
              </Suspense>
            </LightTooltip>
          )}

          {/* Settings Button for specific pages */}
          {pathName !== ModulePath.superAdmin &&
            isUnactiveProjectPage &&
            !isActiveProjectPageWithoutBackButton && (
              <LightTooltip title="Settings">
                <IconButton
                  onClick={() => navigate(ModulePath.settings)}
                  size={getIconSize()}
                  sx={{
                    color: theme.palette.icon.main, // dynamic icon color
                    "&:hover": {
                      color: theme.palette.primary.main, // optional: highlight on hover
                      backgroundColor: alpha(theme.palette.primary.main, 0.12), // soft hover
                    },
                  }}
                >
                  <SettingsOutlinedIcon />
                </IconButton>
              </LightTooltip>
            )}

          {/* Profile Section */}
          <LightTooltip title="Profile">
            <Box
              className="flex items-center cursor-pointer"
              onClick={handleClickProfilePopup}
              sx={{
                fontSize: "1.2rem",
                alignItems: "center",
                fontWeight: 700,
                color: "hsl(var(--primary))", // theme green
                lineHeight: 1,
                borderRadius: "50%",
                justifyContent: "center",
              }}
            >
              {/* Avatar */}
              <Box
                className="flex items-center justify-center"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  overflow: "hidden",
                  mr: isMobile ? 0 : 1,
                }}
              >
                {userDetail?.profilePicUrl ? (
                  <MediaComponent
                    name={`Profile pic: ${userDetail?.firstName}`}
                    type={"image"}
                    variant={"circle"}
                    url={userDetail?.profilePicUrl}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: "25px",
                      height: "25px",
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary,

                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    {userDetail?.firstName?.charAt(0) || "-"}
                  </Avatar>
                )}
              </Box>

              {/* User Info - Hidden on mobile */}
              {!isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.palette.text.primary, // main text

                    minWidth: 0,
                    mr: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary, // main text

                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100px",
                      fontSize: getFontSize("userInfo"),
                    }}
                  >
                    {userDetail?.firstName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100px",
                      fontSize: getFontSize("userRole"),
                      fontWeight: 500,
                    }}
                  >
                    {userDetail?.role || "Admin"}
                  </Typography>
                </Box>
              )}

              {/* Dropdown Arrow */}

              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {/* Dropdown Arrow */}
                <KeyboardArrowDownOutlined
                  fontSize="small"
                  sx={{ color: theme.palette.icon.main }}
                />
              </Box>
            </Box>
          </LightTooltip>

          <Box
            onClick={handleThemeToggle}
            className="
      w-5 h-5 cursor-pointer
      flex items-center justify-center,
      transition
      hover:[hsl(var(--primary)/0.5)]
    "
          >
            {/* Moon – light mode */}
            <LuMoon
              className="w-5 h-5 dark:hidden"
              style={{ color: theme.palette.primary.main }}
            />

            {/* Sun – dark mode */}
            <LuSun
              className="w-5 h-5 hidden dark:block"
              style={{ color: theme.palette.primary.main }}
            />
          </Box>
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        open={Boolean(profileAnchorEl)}
        anchorEl={profileAnchorEl}
        onClose={() => setProfileAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          },
        }}
      >
        <MenuItem
          onClick={() => {}}
          sx={{
            py: 1,
            mx: 0.5,
            mt: 0.5,
            borderRadius: "6px",
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <HelpOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Help & Support"
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={() => {
            navigate(ModulePath.settings);
            setProfileAnchorEl(null);
          }}
          sx={{
            py: 1,
            mx: 0.5,
            borderRadius: "6px",
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Configuration"
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={() => handleLogout()}
          sx={{
            py: 1,
            mx: 0.5,
            mb: 0.5,
            borderRadius: "6px",
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <LogoutOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </Menu>

      {/* Animation Styles */}
      <style>
        {`
                @keyframes underlineExpand {
                    0% {
                        width: 0%;
                        opacity: 0;
                    }
                    100% {
                        width: 70%;
                        opacity: 1;
                    }
                }
                
                @keyframes underlineShrink {
                    0% {
                        width: 50%;
                        opacity: 1;
                    }
                    100% {
                        width: 0%;
                        opacity: 0;
                    }
                }
                `}
      </style>
    </Box>
  );
});

export default ApplicationHeader;
